// Self-service API key management page for the current user.
// Route: /t/:slug/settings/api-key

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { myApiKeyApi } from '@/lib/api/myApiKey'
import { generateAPIKey, hashAPIKey } from '@/lib/api/apiUsers'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/lib/hooks/use-toast'
import { Key, Copy, Check, AlertTriangle, Download, FileJson } from 'lucide-react'
import { buildCredentialsFile, downloadCredentialsFile } from '@/lib/utils/credentials-file'

export function MyApiKeyPage() {
  const { slug, tenantId } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const client = myApiKeyApi(api)

  const [revokeOpen, setRevokeOpen] = useState(false)
  const [keyDialogOpen, setKeyDialogOpen] = useState(false)
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null)
  const [connectionKey, setConnectionKey] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [downloaded, setDownloaded] = useState(false)

  const { data: status, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.users.myApiKey(slug),
    queryFn: () => client.getStatus(),
  })

  async function handleGenerate() {
    setGenerating(true)
    try {
      const key = await generateAPIKey()
      const hash = await hashAPIKey(key)
      const prefix = key.substring(0, 8)
      await client.activate({ key_hash: hash, key_prefix: prefix })

      // Build and auto-download credentials file
      const creds = buildCredentialsFile(tenantId, key)
      downloadCredentialsFile(creds, prefix)

      setPlaintextKey(key)
      setConnectionKey(creds.connection_key)
      setKeyDialogOpen(true)
      setCopied(false)
      setDownloaded(true)
      queryClient.invalidateQueries({ queryKey: queryKeys.users.myApiKey(slug) })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      toast({ title: 'Failed to generate key', description: msg, variant: 'destructive' })
    } finally {
      setGenerating(false)
    }
  }

  const revokeMutation = useMutation({
    mutationFn: () => client.revoke(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.myApiKey(slug) })
      toast({ title: 'API key revoked', description: 'You can generate a new key at any time.' })
      setRevokeOpen(false)
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to revoke key', description: err.message, variant: 'destructive' })
    },
  })

  async function handleCopy() {
    if (!connectionKey) return
    await navigator.clipboard.writeText(connectionKey)
    setCopied(true)
    toast({ title: 'Connection key copied to clipboard' })
  }

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <ErrorState
        message="Failed to load API key status"
        technicalDetail={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  if (!status) return null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">API Key</h1>
        <p className="text-sm text-muted-foreground">
          Manage your personal API key for programmatic access to RiskQuilt.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Your API Key
          </CardTitle>
          <CardDescription>
            API keys allow you to access the RiskQuilt API using your own identity and permissions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!status.has_api_key ? (
            <div className="rounded-md border border-dashed p-6 text-center">
              <Key className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                API access has not been enabled for your account.
              </p>
              <p className="text-sm text-muted-foreground">
                Contact your tenant administrator to request API access.
              </p>
            </div>
          ) : status.key_status === 'revoked' ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
                  Revoked
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Your API key has been revoked by an administrator.
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Contact your tenant administrator to restore API access.
              </p>
            </div>
          ) : status.key_status === 'unactivated' ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                  Ready to activate
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Your admin has enabled API access. Generate your key to get started.
                </span>
              </div>
              <Button onClick={handleGenerate} disabled={generating}>
                <Key className="mr-2 h-4 w-4" />
                {generating ? 'Generating...' : 'Generate API Key'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                  Active
                </Badge>
                {status.key_prefix && (
                  <code className="rounded bg-muted px-2 py-1 text-sm">
                    {status.key_prefix}...
                  </code>
                )}
              </div>
              <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                {status.activated_at && (
                  <div>
                    <dt className="text-muted-foreground">Generated</dt>
                    <dd>{new Date(status.activated_at).toLocaleString()}</dd>
                  </div>
                )}
                {status.last_used_at && (
                  <div>
                    <dt className="text-muted-foreground">Last used</dt>
                    <dd>{new Date(status.last_used_at).toLocaleString()}</dd>
                  </div>
                )}
              </dl>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRevokeOpen(true)}>
                  Revoke &amp; Regenerate
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revoke confirmation */}
      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API key</AlertDialogTitle>
            <AlertDialogDescription>
              Your current API key will be immediately invalidated. Any scripts or
              integrations using it will stop working. You can generate a new key
              straight away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revokeMutation.mutate()}
              disabled={revokeMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke Key'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Key display dialog (shown once after generation) */}
      <Dialog open={keyDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setPlaintextKey(null)
          setConnectionKey(null)
          setCopied(false)
        }
        setKeyDialogOpen(open)
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              Your credentials file has been downloaded. You can also copy the connection
              key below for use with LLM tools.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {downloaded && (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3">
                <Download className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <p className="text-sm text-green-800">
                  Credentials file downloaded. Attach this file when connecting AI tools
                  like ChatGPT or Claude.
                </p>
              </div>
            )}

            <div>
              <p className="mb-1.5 text-sm font-medium">Connection key</p>
              <div className="flex items-center gap-2 rounded-md bg-muted p-3">
                <code className="flex-1 break-all text-xs">{connectionKey}</code>
                <Button variant="ghost" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Paste this into any LLM tool that asks for your RiskQuilt connection key.
              </p>
            </div>

            {plaintextKey && (
              <div>
                <p className="mb-1.5 text-sm font-medium">Raw API key</p>
                <code className="block break-all rounded-md bg-muted p-3 text-xs">{plaintextKey}</code>
              </div>
            )}

            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800">
                Store the credentials file securely. It cannot be re-downloaded.
                If you lose it, revoke and generate a new key.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setKeyDialogOpen(false); setPlaintextKey(null); setConnectionKey(null) }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
